import { Injectable } from "@angular/core";
import { CanvasConfig } from "@utils/canvas-configuration";
import { Color, Intersection, Material, Mesh, MeshBasicMaterial, Object3D, Vector2, Vector3 } from "three";
import { AlteredShapeConfiguration, Object3DUserData } from "../../../../../utils/shape";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DynamicGenerateShape } from "../face-identifier/face-identifier.model";
import { InteractionService } from "./interaction.service";
import { AuxiliaryObjectType, MeshType } from "../../../utils/shape-type";
import { FaceIdentifierService } from "../face-identifier/face-identifier.service";
import { MatSnackBarMessages } from "../mat-snackbar/mat-snackbar.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { ColorCodes } from "~/src/utils/color-constants";

@Injectable({
    providedIn: "root",
})
export class HelperService {
    public cellReferenceForShapes!: { row: number; column: number; cell: number };
    public setUuidIsGround = new Set<String>();
    public foundShapeOnBottom: Mesh[] = [];

    constructor(
        private faceIdentifierService: FaceIdentifierService,
        private snackBar: MatSnackBar,
    ) {}

    /*
  This method is used to get the location y for all the shapes with respect to the ground.
  */
    public getGroundLocationY(): number {
        // Here we make the shape sit on the grid avoiding them to go inside.
        return (
            CanvasConfig.planePostionIn_Y_Coordinate +
            AlteredShapeConfiguration.standardHeigthOfShape / AlteredShapeConfiguration.divisorAdjustYPosition
        );
    }

    /*
    This method will return the nearest coordinates of the grid on the plane
    helps to generate or drop the shapes on the nearest grid.
    */
    public getNearestGridCords(twoDCords: Vector2): Vector2 {
        /*
        Vector2 and Vector2 has only two coordinates 'x' and 'y', but in our case we are
        considering 'y' as 'z' since y is alredy been defined, 'x' and 'z' are calculated and altered for any shape
        to place it on the grid.
        */
        // Calculate the nearest grid intersection in X and Z directions.
        const cellSize = CanvasConfig.cellSize;
        const nearestGrid = twoDCords
            .clone()
            .addScalar(cellSize / 2)
            .divideScalar(cellSize)
            .floor()
            .multiplyScalar(cellSize);

        return new Vector2(nearestGrid.x, nearestGrid.y);
    }

    // This will calculate the row and column of the grid if passed the position of the shape.
    public calculateRowAndColumn(shape: Mesh): {
        row: number;
        column: number;
        cell: number;
    } {
        const cellSize = CanvasConfig.cellSize;

        const row = shape.position.z / cellSize;
        const column = shape.position.x / cellSize;
        const cell = Math.round(Math.abs((shape.position.y - CanvasConfig.planePositionOffset) / cellSize));
        return { row, column, cell };
    }

    public calculatePosition(
        intersects: Intersection[],
        loc: Vector3,
        isFromDragDrop: boolean,
        copiedObjects?: string,
        interactionService?: InteractionService,
    ): { invalidJoin: any; invalidPointGeneration: any; loc: Vector3 } | null {
        let location = new Vector3(loc.x, loc.y, loc.z);
        let invalidJoin = false;
        let invalidPointGeneration = false;
        let hitObject = intersects[0].object as Mesh;
        let point = intersects[0].point;
        const isGridNotAvailable = interactionService?.checkIsGridNotAvailable(hitObject, point);

        if (isGridNotAvailable) {
            this.snackBar.open(MatSnackBarMessages.ErrorMessage5, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.MEDIUM,
            });
            return null;
        }

        const restrictedShapes = [
            MeshType.RightTriangle,
            // MeshType.ConcavePrism,
            MeshType.HalfCylinder,
            MeshType.CurvedCube,
        ];

        let objectName = intersects[0].object.name;
        if (objectName === AuxiliaryObjectType.Plane) {
            location.x = point.x; // GIVES THE POINT OF INTERSECTION IN WORLD COORDS
            location.y = point.y;
            location.z = point.z;

            // Here we make the shape sit on the grid avoiding them to go inside.
            location.y = this.getGroundLocationY();
        } else {
            location.x = point.x; // GIVES THE POINT OF INTERSECTION IN WORLD COORDS
            location.y = point.y;
            location.z = point.z;
            if (this.faceIdentifierService.invalidPointOfGeneration) {
                invalidPointGeneration = true;
            } else if (this.faceIdentifierService.state.rightFace) {
                if (
                    this.faceIdentifierService.invalidJoin &&
                    restrictedShapes.includes(hitObject.name as MeshType) &&
                    restrictedShapes.includes(copiedObjects as MeshType) &&
                    hitObject.name !== copiedObjects
                ) {
                    invalidJoin = true;
                } else if (
                    this.faceIdentifierService.invalidJoin &&
                    !restrictedShapes.includes(copiedObjects as MeshType)
                ) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.z = location.z - DynamicGenerateShape.AdjustmentConstant;
                    location.y = hitObject.position.y;
                }
            } else if (this.faceIdentifierService.state.leftFace) {
                if (
                    this.faceIdentifierService.invalidJoin &&
                    restrictedShapes.includes(hitObject.name as MeshType) &&
                    restrictedShapes.includes(copiedObjects as MeshType) &&
                    hitObject.name !== copiedObjects
                ) {
                    invalidJoin = true;
                } else if (
                    this.faceIdentifierService.invalidJoin &&
                    !restrictedShapes.includes(copiedObjects as MeshType)
                ) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.z = location.z + DynamicGenerateShape.AdjustmentConstant;
                    location.y = hitObject.position.y;
                }
            } else if (this.faceIdentifierService.state.topFace) {
                if (
                    this.faceIdentifierService.invalidJoin &&
                    restrictedShapes.includes(hitObject.name as MeshType) &&
                    restrictedShapes.includes(copiedObjects as MeshType) &&
                    hitObject.name !== copiedObjects
                ) {
                    invalidJoin = true;
                } else if (
                    this.faceIdentifierService.invalidJoin &&
                    !restrictedShapes.includes(copiedObjects as MeshType)
                ) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.y = hitObject.position.y + AlteredShapeConfiguration.standardHeigthOfShape;
                }
            } else if (this.faceIdentifierService.state.bottomFace) {
                if (this.faceIdentifierService.invalidJoin) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.y = hitObject.position.y - AlteredShapeConfiguration.standardHeigthOfShape;
                }
            } else if (this.faceIdentifierService.state.frontFace) {
                if (this.faceIdentifierService.invalidJoin) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.x = location.x + DynamicGenerateShape.AdjustmentConstant;
                    location.y = hitObject.position.y;
                }
            } else if (this.faceIdentifierService.state.curveFace) {
                if (this.faceIdentifierService.invalidJoin) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.y = hitObject.position.y;
                }
            } else if (this.faceIdentifierService.state.backFace && !this.faceIdentifierService.state.topFace) {
                if (this.faceIdentifierService.invalidJoin) {
                    invalidJoin = true;
                } else {
                    invalidJoin = false;
                    invalidPointGeneration = false;
                    location.x = location.x - DynamicGenerateShape.AdjustmentConstant;
                    location.y = hitObject.position.y;
                }
            } else if (this.faceIdentifierService.invalidJoin) {
                invalidJoin = true;
            }
        }
        return {
            invalidJoin,
            invalidPointGeneration,
            loc: new Vector3(location.x, location.y, location.z),
        };
    }

    public getReferenceMesh(oldReferenceMesh: Mesh, objects: Mesh[]): Mesh {
        return objects.reduce((minMesh, currentMesh) => {
            const currentCell = currentMesh.userData[Object3DUserData.cellReference]?.cell;
            const minCell = minMesh.userData[Object3DUserData.cellReference]?.cell;

            if (currentCell !== null && (minCell === null || currentCell < minCell)) {
                return currentMesh;
            }
            return minMesh;
        }, oldReferenceMesh);
    }
}
